/**
 * Shared AdSense / ad-surface policy — single source of truth for client id,
 * restricted paths/subdomains, and in-flow vs overlay allow decisions.
 */

import { DEFAULT_ADSENSE_PUBLISHER_ID } from './adsenseReportingConfig.js';

/** Publisher client id used by pagead2 adsbygoogle.js (`ca-pub-…`). */
export const ADSENSE_CLIENT_ID = `ca-${DEFAULT_ADSENSE_PUBLISHER_ID}`;

/** Stable DOM id for the client-injected AdSense script (dedupe). */
export const ADSENSE_SCRIPT_ID = 'google-adsense-script';

/** Platform path prefixes where ads must never load. */
export const AD_RESTRICTED_PATH_PREFIXES: readonly string[] = [
  '/alerts',
  '/for-officers',
  '/for-professionals',
  '/write-for-apt',
  '/secondary-employment/for-employers',
  '/employer-signup',
  '/professional-signup',
  '/dashboard',
  '/backoffice',
  '/admin',
  '/k9',
  '/k9-ops',
  '/me',
  '/teach',
  '/provider-signup',
  '/training-provider-services',
  '/tac-shop/checkout',
  '/tac-shop/order',
  '/payment-success',
] as const;

/** Product subdomains that are platform surfaces (no ads). */
export const AD_RESTRICTED_SUBDOMAINS: readonly string[] = [
  'dash',
  'backoffice',
  'k9',
  'me',
  'teach',
  'support',
  'docs',
  'api',
] as const;

export type AdSurfaceContext = {
  pathname: string;
  subdomain?: string | null;
  showAds: boolean;
  /** Literal env: ADSENSE_SUPPRESS_GOOGLE_OVERLAYS / VITE_… === 'true' */
  suppressGoogleOverlays?: boolean;
};

function normalizePathname(pathname: string): string {
  if (!pathname) {
    return '/';
  }
  const q = pathname.indexOf('?');
  const h = pathname.indexOf('#');
  let end = pathname.length;
  if (q >= 0) {
    end = Math.min(end, q);
  }
  if (h >= 0) {
    end = Math.min(end, h);
  }
  const path = pathname.slice(0, end) || '/';
  return path.startsWith('/') ? path : `/${path}`;
}

function isEventDetailPath(pathname: string): boolean {
  const path = normalizePathname(pathname);
  return path.startsWith('/events/') && path.length > '/events/'.length;
}

/** True when pathname is a platform surface or conversion page where ads should never load. */
export function isAdRestrictedPath(pathname: string): boolean {
  const path = normalizePathname(pathname);
  if (isEventDetailPath(path)) {
    return true;
  }
  return AD_RESTRICTED_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}

/**
 * Conversion-aware ad restriction helper for flows that want public index pages
 * to remain eligible while blocking detail/checkout surfaces.
 */
export function isConversionAdRestrictedPath(pathname: string): boolean {
  return isAdRestrictedPath(pathname);
}

/** True when subdomain is a restricted platform host. */
export function isAdRestrictedSubdomain(subdomain: string | null | undefined): boolean {
  if (!subdomain) {
    return false;
  }
  const s = subdomain.trim().toLowerCase();
  return AD_RESTRICTED_SUBDOMAINS.includes(s);
}

/** True when either path or subdomain forbids ads. */
export function isAdRestrictedSurface(
  pathname: string,
  subdomain?: string | null,
): boolean {
  return isAdRestrictedPath(pathname) || isAdRestrictedSubdomain(subdomain);
}

/**
 * In-flow Auto Ads / APT display slots may run when the user is eligible and
 * the surface is public (not restricted).
 */
export function shouldAllowInFlowAutoAds(ctx: AdSurfaceContext): boolean {
  if (!ctx.showAds) {
    return false;
  }
  if (isAdRestrictedSurface(ctx.pathname, ctx.subdomain)) {
    return false;
  }
  return true;
}

/**
 * Google vignette / interstitial / anchor overlays. Default off when suppress
 * flag is set (APT owns interstitial-style overlays).
 */
export function shouldAllowGoogleOverlayAds(ctx: AdSurfaceContext): boolean {
  if (!shouldAllowInFlowAutoAds(ctx)) {
    return false;
  }
  if (ctx.suppressGoogleOverlays === true) {
    return false;
  }
  return true;
}

/** Server/client: literal `true` for ADSENSE_SUPPRESS_GOOGLE_OVERLAYS. */
export function isAdSenseSuppressGoogleOverlaysEnabled(
  env: Record<string, string | undefined> = typeof process !== 'undefined' ? process.env : {},
): boolean {
  return env.ADSENSE_SUPPRESS_GOOGLE_OVERLAYS === 'true';
}

/** Server: literal `true` for APT_OVERLAY_COORDINATOR_ENABLED. */
export function isAptOverlayCoordinatorEnabled(
  env: Record<string, string | undefined> = typeof process !== 'undefined' ? process.env : {},
): boolean {
  return env.APT_OVERLAY_COORDINATOR_ENABLED === 'true';
}

/** Vite client: literal `true` for VITE_ADSENSE_SUPPRESS_GOOGLE_OVERLAYS. */
export function isViteAdSenseSuppressGoogleOverlaysEnabled(
  viteEnvValue: string | boolean | undefined,
): boolean {
  return viteEnvValue === true || viteEnvValue === 'true';
}

/** Vite client: literal `true` for VITE_APT_OVERLAY_COORDINATOR_ENABLED. */
export function isViteAptOverlayCoordinatorEnabled(
  viteEnvValue: string | boolean | undefined,
): boolean {
  return viteEnvValue === true || viteEnvValue === 'true';
}
